import { useState, useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import BuscaGlobal from "../components/BuscaGlobal";
import { getAlertas } from "../api/dashboard.api";
import { getUser } from "../api/auth.api";
import { getConfiguracaoTelegram, enviarPrazosAgora } from "../api/configuracao.api";
import { getProjetosCompleto } from "../api/projetos.api";
import { devePerguntar, pedirPermissao, notificarUrgentes } from "../utils/browserNotificacoes";
import { useAlertaTarefas } from "../utils/tarefasAlerta";

export default function MainLayout() {
  const [alertCount, setAlertCount] = useState(0);
  const [collapsed,  setCollapsed]  = useState(false);
  const [buscaOpen,  setBuscaOpen]  = useState(false);
  const tarefasUrgentes = useAlertaTarefas();

  useEffect(() => {
    async function load() {
      const res = await getAlertas();
      setAlertCount(res.alertas?.length || 0);
    }
    load();
  }, []);

  /* ── Pedir permissão de notificações na primeira visita ── */
  useEffect(() => {
    if (devePerguntar()) {
      const id = setTimeout(() => pedirPermissao(), 5000);
      return () => clearTimeout(id);
    }
  }, []);

  /* ── Disparar notificação browser quando urgentes são detectadas ── */
  useEffect(() => {
    if (tarefasUrgentes.length > 0) {
      const urgentes = tarefasUrgentes.map(t => ({
        titulo: t.descritivo || t._ticket?.descricao_customizada || t._ticket?.subject || `#${t.id_ticket}`,
        urgencia: (() => {
          if (!t.data_entrega) return null;
          const diff = Math.floor((new Date(t.data_entrega) - new Date(new Date().toDateString())) / 864e5);
          return diff < 0 ? "vencida" : diff === 0 ? "hoje" : diff === 1 ? "amanha" : null;
        })(),
      })).filter(t => t.urgencia);
      notificarUrgentes(urgentes);
    }
  }, [tarefasUrgentes]);

  /* ── Pré-aquecimento do cache de Tarefas ──────────────────────────────
     Carrega projetos + todas as tarefas em background 2s após o login.
     Quando o usuário navegar para qualquer página de Tarefas, os dados
     já estarão em cache e a página abre instantaneamente.
  ──────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const id = setTimeout(async () => {
      try {
        await getProjetosCompleto(false, 2); // aquece projetos + tarefas numa única chamada
      } catch {} // silencioso — é apenas pre-fetch
    }, 2000);
    return () => clearTimeout(id);
  }, []);

  /* ── Keep-alive: pinga o Render a cada 8 min para evitar cold start ── */
  useEffect(() => {
    const url = `${process.env.REACT_APP_API_URL}/sprint/resumo`;
    const id  = setInterval(() => fetch(url).catch(() => {}), 8 * 60_000);
    return () => clearInterval(id);
  }, []);

  /* ── Scheduler Telegram: substitui o artisan schedule:run ──────────
     O Render free tier não executa processos em background, então o
     frontend assume o papel de disparar o envio no horário correto.
  ──────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    let cfg     = null;
    let cfgTs   = 0;
    let sending = false;
    const CFG_TTL = 5 * 60_000;

    async function tick() {
      if (sending) return;
      try {
        if (!cfg || Date.now() - cfgTs > CFG_TTL) {
          cfg   = await getConfiguracaoTelegram();
          cfgTs = Date.now();
          // Semeia localStorage com o último envio registrado no backend
          if (!localStorage.getItem("tg_last_send") && cfg.telegram_prazos_ultimo_envio) {
            localStorage.setItem("tg_last_send",
              String(new Date(cfg.telegram_prazos_ultimo_envio).getTime()));
          }
        }
        if (cfg.telegram_prazos_ativo !== "true")          return;
        if (!cfg.telegram_bot_token || !cfg.telegram_chat_id) return;

        const hora  = new Date().getHours();
        const hIni  = parseInt(cfg.telegram_hora_inicio || "8",  10);
        const hFim  = parseInt(cfg.telegram_hora_fim    || "21", 10);
        if (hora < hIni || hora >= hFim) return;

        const intervaloMs = parseInt(cfg.telegram_prazos_intervalo_horas || "1", 10) * 3_600_000;
        const lastSend    = parseInt(localStorage.getItem("tg_last_send") || "0", 10);
        if (Date.now() - lastSend < intervaloMs) return;

        sending = true;
        await enviarPrazosAgora();
        localStorage.setItem("tg_last_send", String(Date.now()));
        cfg = null; // força reload do config na próxima tick
      } catch { /* falha silenciosa — tenta na próxima tick */ }
      finally  { sending = false; }
    }

    tick(); // verifica ao montar
    const id = setInterval(tick, 60_000); // verifica a cada minuto
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setBuscaOpen(v => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!getUser()) return <Navigate to="/login" replace />;

  return (
    <div className="app-container">
      <Sidebar
        alertCount={alertCount}
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
        onBusca={() => setBuscaOpen(true)}
      />
      <div className="main-content">
        <Outlet />
      </div>
      <BuscaGlobal open={buscaOpen} onClose={() => setBuscaOpen(false)} />
    </div>
  );
}
